import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle, FileText, Minimize2 } from 'lucide-react';
import { VCardData } from '@/lib/vcard-utils';

interface VCardOptionsProps {
  data: VCardData;
  onChange: (data: Partial<VCardData>) => void;
}

export function VCardOptions({ data, onChange }: VCardOptionsProps) {
  return (
    <div className="space-y-6">
      {/* vCard Version */}
      <div className="space-y-3">
        <Label className="text-base font-medium">vCard Version</Label>
        <RadioGroup
          value={data.vcardVersion}
          onValueChange={(value) => onChange({ vcardVersion: value as '3.0' | '4.0' })}
          className="space-y-2"
        >
          <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="3.0" id="v3" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="v3" className="font-medium cursor-pointer">
                vCard 3.0 (Recommended)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Widest compatibility with all devices and apps. Works with older phones and email clients.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="4.0" id="v4" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="v4" className="font-medium cursor-pointer">
                vCard 4.0
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                More features (anniversary, improved social links). May not work with older devices.
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Include Photo */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-base font-medium">Include Photo in QR</Label>
            <p className="text-sm text-muted-foreground">
              Embed photo reference in the vCard data
            </p>
          </div>
          <Switch
            checked={data.includePhoto}
            onCheckedChange={(checked) => onChange({ includePhoto: checked })}
            disabled={!data.photoUrl}
          />
        </div>
        
        {data.includePhoto && (
          <div className="flex items-start gap-2 p-3 bg-secondary/50 rounded-lg text-sm">
            <AlertTriangle className="h-4 w-4 text-secondary-foreground shrink-0 mt-0.5" />
            <div className="text-secondary-foreground">
              <p className="font-medium">Warning: Larger QR Code</p>
              <p className="text-xs mt-1">
                Including photo data makes the QR code denser and may reduce scannability. 
                The photo URL will be included, not the actual image data.
              </p>
            </div>
          </div>
        )}
        
        {!data.photoUrl && (
          <p className="text-xs text-muted-foreground">
            Upload a photo in the Personal Info section to enable this option.
          </p>
        )}
      </div>

      {/* Minimal vCard */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="flex items-center gap-2 text-base font-medium">
              <Minimize2 className="h-4 w-4" />
              Minimal vCard
            </Label>
            <p className="text-sm text-muted-foreground">
              Only include name and primary contact info
            </p>
          </div>
          <Switch
            checked={data.minimalVCard}
            onCheckedChange={(checked) => onChange({ minimalVCard: checked })}
          />
        </div>
        
        {data.minimalVCard && (
          <div className="flex items-start gap-2 p-3 bg-muted rounded-lg text-sm">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-muted-foreground">
              <p>The QR code will only contain:</p>
              <ul className="list-disc list-inside mt-1 text-xs">
                <li>Full name</li>
                <li>Primary phone number</li>
                <li>Primary email address</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
